import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Calendar, CheckCircle2, ArrowUpRight, Compass, Shield, Heart } from "lucide-react";

export default function Programs() {
  const programsList = [
    {
      level: "Foundation Pathway",
      name: "Emotional Freedom Blueprint",
      tagline: "A somatic foundation for understanding emotions, triggers, and setting neurological calm.",
      forWho: "Individuals seeking a grounded, practical methodology to transition from reactive overwhelm and emotional fatigue into conscious self-possession.",
      offers: [
        "Interactive Trigger Mapping: Systematically chart your recurring emotional triggers.",
        "Somatic Settle Techniques: Learn physical and nervous-system down-regulation practices.",
        "Subconscious Pattern Audits: Identify childhood protective mechanisms running automatically.",
        "Bedrock Self-Worth Practices: Practical reflection and emotional sovereignty templates."
      ],
      transformation: "Shift from reactive, defensive emotional overwhelm into clear, intentional responses and sustained inner calm.",
      accent: "border-[#FF8C00]/20 hover:border-[#FF8C00]/50",
      pillBg: "bg-[#FF8C00]/15 text-[#FF8C00]"
    },
    {
      level: "Mastery Chamber",
      name: "Self Mastery",
      tagline: "A journey into self-worth, identity, confidence, boundaries and inner leadership.",
      forWho: "Those ready to stop people-pleasing, establish absolute boundaries without guilt, and rebuild their default identity around self-love and self-respect.",
      offers: [
        "Sovereign Boundaries Framework: Formulate clear, non-negotiable personal boundaries.",
        "Subconscious Identity Audit: Deconstruct outdated limiting beliefs and scripts.",
        "Radical Self-Love Rituals: Practical daily rituals to solidify self-worth and confidence.",
        "Inner Leadership Incubator: Grounded personal development and executive presence practices."
      ],
      transformation: "Shift from chronic self-doubt and people-pleasing into deep, unshakeable self-worth and inner confidence.",
      accent: "border-[#C87D53]/20 hover:border-[#C87D53]/50",
      pillBg: "bg-[#C87D53]/15 text-[#C87D53]"
    },
    {
      level: "Mastery Chamber",
      name: "Relationship Mastery",
      tagline: "A practical journey toward healthier communication, emotional awareness and conscious relationships.",
      forWho: "Couples and individuals seeking to transform automatic defense patterns, develop high empathy, and co-create relationships of absolute safety.",
      offers: [
        "Conscious Dialog Protocols: Non-defensive, active-listening communication systems.",
        "Trigger Safe-Zones: Create mutual agreements for when somatic arousal is high.",
        "Interpersonal Mirror Mapping: Understand what your partner's reaction is reflecting.",
        "Co-Regulation Rituals: Share practices to soothe nervous systems together."
      ],
      transformation: "Shift from reactive power-struggles and tension into conscious, safe, and emotionally mature connection.",
      accent: "border-[#2C4A3E]/30 hover:border-[#2C4A3E]/60",
      pillBg: "bg-[#2C4A3E]/30 text-emerald-400"
    },
    {
      level: "Mastery Chamber",
      name: "Emotional Mastery",
      tagline: "A deeper journey into emotional intelligence, awareness and conscious emotional responses.",
      forWho: "Seekers wanting to move beyond basic triggers into advanced EQ, mastering the art of converting intense raw emotions into creative energy.",
      offers: [
        "Advanced Somatic Alchemy: Convert intense emotional states (anger, grief) into productive force.",
        "The Somatic Dialogue Method: Learn to decode complex underlying neurological messages.",
        "Subconscious Archive Release: Safely clear stored physical tension and trauma states.",
        "BEDROCK Presence Protocols: Learn to hold absolute calm while others are in high trigger."
      ],
      transformation: "Shift from feeling controlled by intense emotional states into enjoying total inner stability and creative sovereignty.",
      accent: "border-[#00D2FF]/20 hover:border-[#00D2FF]/50",
      pillBg: "bg-[#00D2FF]/15 text-[#00D2FF]"
    },
    {
      level: "Mastery Chamber",
      name: "Abundance Mastery",
      tagline: "A conscious approach to wealth, abundance, beliefs, receiving and purposeful action.",
      forWho: "Action-takers wanting to dissolve deep ancestral money shame, lift their default financial ceiling, and create ethical impact.",
      offers: [
        "Wealth Blueprint Audits: Expose subconscious financial glass ceilings and scripts.",
        "The Art of Somatic Receiving: Recondition the nervous system to feel safe with large sums.",
        "Purposeful Value Creation: Practical marketing and branding from absolute alignment.",
        "Co-Creation Principles: Grounded financial mindset and conscious asset allocation."
      ],
      transformation: "Shift from scarcity-based decisions and money avoidance into conscious receiving and ethical wealth creation.",
      accent: "border-[#C87D53]/20 hover:border-[#C87D53]/50",
      pillBg: "bg-[#C87D53]/20 text-[#C87D53]"
    },
    {
      level: "Integrated Ultimate Path",
      name: "ESHA Life Mastery",
      tagline: "An integrated ultimate journey focused on consciously designing life around purpose, leadership and impact.",
      forWho: "Advanced students and leaders ready to integrate EQ, energy work, and practical action to construct their life as a conscious work of art.",
      offers: [
        "Sovereign Life Design: Draft an active blueprint for your career, relationship, and health.",
        "Energetic Resonance Mastery: Align your default home vibration with your highest vision.",
        "Legacy Leadership Incubation: Architect systems of service and contribution.",
        "Personal Integration Mentorship: Guided integration support with the ESHA lead team."
      ],
      transformation: "Shift from reactive, survival-mode living into sovereign, intentional, and high-impact life design.",
      accent: "border-purple-500/20 hover:border-purple-500/50",
      pillBg: "bg-purple-500/15 text-purple-300"
    }
  ];

  return (
    <div className="min-h-screen pt-28 pb-20 bg-[#050505] text-[#F6F4EE] relative" data-testid="programs-page">
      {/* Background spotlights */}
      <div className="absolute inset-0 glow-radial opacity-20 pointer-events-none" />
      <div className="absolute bottom-20 right-20 h-96 w-96 glow-copper opacity-10 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
        {/* Header Intro */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <span className="font-mono text-xs tracking-[0.3em] uppercase text-[#00D2FF] px-3 py-1.5 border border-[#00D2FF]/20 rounded-full bg-[#111111]/80 font-bold">
            ESHA Pathways
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-[#F6F4EE] leading-tight mt-6 mb-6">
            Our Transformational <span className="text-blue-shimmer italic">Programs</span>
          </h1>
          <p className="font-sans text-base text-[#AAA29E] leading-relaxed">
            Every ESHA offering is a step in a structured journey toward self-connection and conscious creation. We focus on deep, sustained, practical changes rather than quick-fix motivation.
          </p>
        </div>

        {/* Programs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {programsList.map((program, idx) => (
            <div
              key={idx}
              className={`bg-[#0F0F0F]/80 border rounded-2xl p-8 sm:p-10 flex flex-col justify-between shadow-lg transition-all duration-500 hover:scale-[1.01] hover:shadow-[0_15px_40px_rgba(0,0,0,0.8)] group ${program.accent}`}
              data-testid={`program-card-${idx}`}
            >
              <div>
                {/* Level Pill */}
                <div className="flex justify-between items-center mb-6">
                  <span className={`font-mono text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1 rounded-full ${program.pillBg}`}>
                    {program.level}
                  </span>
                  <Compass className="h-4 w-4 text-[#FF8C00]/40 group-hover:text-[#FF8C00] transition-colors" />
                </div>

                {/* Program Name */}
                <h2 className="font-serif text-2xl sm:text-3xl text-[#F6F4EE] font-light leading-snug mb-3">
                  {program.name}
                </h2>

                {/* Promise tagline */}
                <p className="font-sans text-sm text-[#FF8C00] font-semibold mb-6">
                  {program.tagline}
                </p>

                {/* Who is it for */}
                <div className="mb-6 bg-[#050505] p-4 rounded-xl border border-[#FF8C00]/15">
                  <h4 className="text-xs uppercase tracking-widest font-mono text-[#AAA29E] font-bold mb-1">
                    Who It Is For:
                  </h4>
                  <p className="font-sans text-xs sm:text-sm text-[#AAA29E] leading-relaxed">
                    {program.forWho}
                  </p>
                </div>

                {/* What it offers */}
                <div className="mb-8">
                  <h4 className="text-xs uppercase tracking-widest font-mono text-[#F6F4EE] font-bold mb-3">
                    What The Program Offers:
                  </h4>
                  <ul className="space-y-2.5">
                    {program.offers.map((offer, oIdx) => (
                      <li key={oIdx} className="flex gap-2.5 items-start text-xs sm:text-sm text-[#AAA29E] leading-relaxed">
                        <CheckCircle2 className="h-4 w-4 text-[#00D2FF] shrink-0 mt-0.5" />
                        <span>{offer}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Transformation and CTA */}
              <div className="border-t border-[#FF8C00]/15 pt-6 mt-6">
                <div className="mb-6">
                  <h4 className="text-xs uppercase tracking-widest font-mono text-emerald-400 font-bold mb-1">
                    Transformation:
                  </h4>
                  <p className="font-serif italic text-sm text-[#F6F4EE] leading-relaxed">
                    {program.transformation}
                  </p>
                </div>

                <Link
                  to="/offerings"
                  className="w-full py-3.5 bg-gradient-to-r from-[#FF8C00] to-[#FF4500] hover:from-[#FFA500] text-white text-xs uppercase tracking-widest font-mono font-bold rounded-xl flex items-center justify-center gap-2 transition-all duration-300 shadow-md"
                  data-testid={`program-cta-link-${idx}`}
                >
                  Explore Offerings & Enroll
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
